import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { MachineLogicFactory } from '@vse/main';
import { Spinner, Redirect } from '@src/components';
import * as mqtt_client from '../Main/mqtt_instance';
import { isOnVentionPendant } from '@src/Main/main';
import {
  AppLauncher,
  ConfigChecker,
  Jogger,
  MachineApp,
  MachineConfig,
  MachineLogic,
  Menu,
  NetworkConfig,
  Onboarding,
  UiBuilder,
} from '../views';
import { RemoteSupportPage } from '@ventionco/remote-support-components';
import { Pages } from '../Pages';
import { AppLayout, NavbarLayout } from '../layouts';
import { createUnmountReactAndNavigate } from '../hooks';
import { setNavigate } from '../NavigationService';
import { getNavigationLinks } from './getNavigationLinks';

/**
 * Creates the app router which will render the pages
 *
 * VERY IMPORTANT NOTE, KEEP READING
 *
 * The app router props values MUST NEVER CHANGE, or the navigation will break.
 * This is because if the props values change, the component will try to re-render.
 * It will create a new router, and all the jQuery code needs to be updated with that new router.
 *
 * We could re-render the whole page, but you'll see flickers.
 * Moreover, the code doesn't track which network calls were already made / already in flight, and we might end up re-doing calls for nothing.
 * If everything was in React, these issues would naturally be fixed.
 *
 * This is super fixable even with jQuery, but it's a larger scope.
 * So if the navigation seems broken, there's a 99% chance that it's because the AppRouter is re-rendering.
 * @param {object} props
 * @param {boolean} props.hasFullAccess A boolean indicating if the user has full access to the control center
 * @param {ConfigInfo} props.configInfo The hardware config
 * @param {Library} props.library The machinelogic library
 * @param {boolean} props.isPendantAvailable A boolean indicating if the pendant is available
 * @return {JSX.Element}
 */
export const AppRouter = ({ hasFullAccess, configInfo, library, isPendantAvailable }) => {
  const [isMachineAppsInitialized, setIsMachineAppsInitialized] = useState(false);

  // We freeze the router so that we can use other hooks in the AppRouter
  // The props still must not change
  // The router needs to be created only once because the codebase can't handle a mutating router (yet)

  // mmConfig may not exist as it is only in vention-control (https://github.com/VentionCo/mm-vention-control/blob/develop/mm-config.sample.json)
  if (!configInfo.mmConfig) {
    console.warn('No mm-config.json file found. Please check MM_CONFIG_PATH in mm-exec-engine.');
  }
  const lockInAppLauncher = Boolean(configInfo.mmConfig?.lock_in_app_launcher);

  // Machine app is only available on the actual pendant when the flag is enabled in mm-config.json
  const isMachineAppAvailable = Boolean(configInfo.mmConfig?.enable_machine_app) && isOnVentionPendant();

  const routeCreationData = {
    isPendantAvailable,
    isMachineAppAvailable,
    hasFullAccess,
    lockInAppLauncher,
    library,
  };
  const routes = lockInAppLauncher
    ? createAppLauncherOnlyRoutes(routeCreationData)
    : createDefaultRoutes(routeCreationData);

  const [router] = useState(createBrowserRouter(routes));

  useEffect(() => {
    // If `router` changes, mqtt_client.initClient won't correctly update the mqtt_client
    // We're not handling this (yet?)
    mqtt_client.initClient(createUnmountReactAndNavigate(router.navigate));
  }, []);

  useEffect(() => {
    // If `router` changes, MachineLogicFactory.initializeMachineApps won't correctly update the navigate callback
    // We're not handling this (yet?)
    MachineLogicFactory.initializeMachineApps(library, createUnmountReactAndNavigate(router.navigate))
      .then(() => setIsMachineAppsInitialized(true));
  }, []);

  useEffect(() => {
    setNavigate(createUnmountReactAndNavigate(router.navigate));
  }, []);

  useEffect(() => {
    // NOTE: HACK! We are navigation straight to the path following view on load...
    router.navigate('/machine-app?type=path_following&view=machineParameters');
  }, [isMachineAppsInitialized]);

  if (!isMachineAppsInitialized) {
    return <Spinner />;
  }

  return <RouterProvider router={router} />;
};

AppRouter.propTypes = {
  hasFullAccess: PropTypes.bool,
  configInfo: PropTypes.object,
  library: PropTypes.object,
  isPendantAvailable: PropTypes.bool,
};

/**
 * @param {boolean} isPendantAvailable A boolean indicating if the pendant is available
 * @param {boolean} isMachineAppAvailable A boolean indicating if the machine app is available
 * @param {boolean} hasFullAccess A boolean indicating if the user has full access to the control center
 * @param {boolean} lockInAppLauncher A boolean indicating if pendant is locked to AppLauncher only
 * @param {Library} library The machinelogic library
 * @return {routes[]} Routes for default pendant view
 */
function createDefaultRoutes ({ isPendantAvailable, isMachineAppAvailable, hasFullAccess, lockInAppLauncher, library }) {
  return [
    {
      path: '/',
      element: <AppLayout
        isPendantAvailable={isPendantAvailable}
        robotAppsService={library.robotAppsService}
      />,
      children: [
        { path: '/', element: <Menu isPendantAvailable={isPendantAvailable} isMachineAppAvailable={isMachineAppAvailable}/> },
        { path: Pages.MENU, element: <Menu isPendantAvailable={isPendantAvailable} isMachineAppAvailable={isMachineAppAvailable}/> },
        { path: Pages.ONBOARDING, element: <Onboarding isPendantAvailable={isPendantAvailable} /> },
        { path: Pages.REMOTE_SUPPORT, element: <RemoteSupportPage isOnPendant={isOnVentionPendant()}/> },
        {
          path: '/',
          element: <NavbarLayout navigationLinks={getNavigationLinks(isMachineAppAvailable, hasFullAccess, lockInAppLauncher)} />,
          children: [
            { path: Pages.MACHINE_CONFIG, element: <MachineConfig library={library} /> },
            { path: Pages.CONFIG_CHECKER, element: <ConfigChecker library={library} /> },
            { path: Pages.NETWORK_CONFIG, element: <NetworkConfig /> },
            { path: Pages.MACHINE_LOGIC, element: <MachineLogic library={library} /> },
            { path: Pages.JOGGER, element: <Jogger robotAppsService={library.robotAppsService}/> },
            { path: Pages.APP_LAUNCHER, element: <AppLauncher library={library} /> },
            { path: Pages.UI_BUILDER, element: <UiBuilder library={library} /> },
            { path: Pages.MACHINE_APP, element: <MachineApp library={library} /> },
          ],
        },
      ],
    },
  ];
}

/**
 * @param {boolean} isPendantAvailable A boolean indicating if the pendant is available
 * @param {boolean} isMachineAppAvailable A boolean indicating if the machine app is available
 * @param {boolean} hasFullAccess A boolean indicating if the user has full access to the control center
 * @param {boolean} lockInAppLauncher A boolean indicating if pendant is locked to AppLauncher only
 * @param {Library} library The machinelogic library
 * @return {RouteObject[]} Routes for default pendant view
 */
function createAppLauncherOnlyRoutes ({ isPendantAvailable, isMachineAppAvailable, hasFullAccess, lockInAppLauncher, library }) {
  return [
    {
      path: '/',
      element: <AppLayout
        isPendantAvailable={isPendantAvailable}
        robotAppsService={library.robotAppsService}
      />,
      children: [
        { path: Pages.REMOTE_SUPPORT, element: <RemoteSupportPage isOnPendant={isOnVentionPendant()}/> },
        {
          path: '/',
          element: <NavbarLayout navigationLinks={getNavigationLinks(isMachineAppAvailable, hasFullAccess, lockInAppLauncher)} />,
          children: [
            { path: Pages.APP_LAUNCHER, element: <AppLauncher library={library} /> },
            { path: '/', element: <Redirect pathname={Pages.APP_LAUNCHER}/> },
            { path: '/*', element: <Redirect pathname={Pages.APP_LAUNCHER}/> },
          ],
        },
      ],
    },
  ];
}
